import { createAsyncAction, errorResult, successResult } from "pullstate";
import * as Sentry from "@sentry/browser";
import { GlobalStore } from "./GlobalStore";
import { Quote } from "../../types/quote";
import { Chain } from "@rainbow-me/rainbowkit";
import { Order, OrderError } from "../../types/order";
import { getSendOrderEndpoint } from "../../utils/orderUtils";
import { Message } from "../../types/message";
import { OrderStatus } from "../../types/orderStatus";
import { getErrorMessageFromOrderError } from "../../config/errorCodes";
import { MODAL_SELECTORS } from "../../components/Modal/instance/modalSelectors";
import { Amount } from "../../types/amount";
import { Permit1Details, Permit2Details } from "../../utils/permitUtils";
import { isPermit2Signature } from "../../utils/permit2Utils";

export type StorePermit = Permit1Details | Permit2Details;
export const sendSignedOrder = createAsyncAction(
  async ({
    chain,
    quote,
    signature,
    permit,
  }: {
    message: Message;
    chain: Chain;
    quote: Quote;
    signature: string;
    permit: StorePermit;
  }) => {
    try {
      const result = await postOrder(chain, quote, signature, permit);
      if (result) {
        return successResult(result);
      }
    } catch (e) {
      Sentry.captureException(`Could not post order`, { extra: { event: e } });
      return errorResult([], `Could not post order for network`, e);
    }
    Sentry.captureException(`Could not post order`);
    return errorResult([], `Could not post order`);
  },
  {
    postActionHook: ({ result }) => {
      if (!result.error) {
        GlobalStore.update(s => {
          if (s.modalSelector === MODAL_SELECTORS.CONFIRM_TRADE) {
            s.modalSelector = undefined;
          }
          s.order = result.payload;
          s.signedMessage = undefined;
        });
      }
    },
  }
);

const postOrder = (
  chain: Chain,
  quote: Quote,
  signature: string,
  permit: StorePermit
): Promise<Order | undefined> => {
  const sendOrderEndpoint = getSendOrderEndpoint(quote, chain);
  /**
   * Sanity checks for order expiry
   */
  try {
    const MS_PER_SEC = "1000";
    const quoteExpiry = quote.expiry.mul(new Amount(MS_PER_SEC)); //convert to ms
    const currentTime = new Amount(Date.now().toString());
    if (currentTime.value.lte(quoteExpiry.value)) {
      const timeToExpiry = currentTime.value.minus(quoteExpiry.value).div(1000).toString();
      console.log(`Quote has ${timeToExpiry} seconds to expiry`);
      Sentry.setTag("timeToExpiry", timeToExpiry);
    } else {
      Sentry.captureException(
        `Attempting to submit an order for an expired quote. Quote expiry: ${quoteExpiry.toString()}, Current time: ${currentTime.toString()}`
      );
      Sentry.setTag("quoteExpiry", quoteExpiry.toString());
      Sentry.setTag("currentTime", currentTime.toString());
    }
  } catch (e) {
    console.log(e);
  }
  const getPermitPayload = (
    permit: StorePermit,
    quote: Quote,
    nonces: Array<{ token: `0x${string}`; nonce: number }>
  ) => {
    // We only send the signatures if the backend requests them
    if (quote.requiredSignatures.length > 0) {
      if (isPermit2Signature(permit)) {
        const tokenAddresses = quote.requiredSignatures;
        const token_nonces = tokenAddresses.map(token => {
          const nonce = nonces.find(nonce => nonce.token === token);
          return nonce ? nonce.nonce : 0;
        });
        const permit2 = {
          permit2: {
            signature: permit.signature,
            approvals_deadline: permit.deadline,
            token_addresses: tokenAddresses,
            token_nonces: token_nonces,
          },
        };
        return permit2;
      } else {
        return {
          permit: {
            signature: permit.signature,
            approvals_deadline: permit.deadline,
          },
        };
      }
    }
  };

  return fetch(sendOrderEndpoint, {
    method: "POST",
    body: JSON.stringify({
      signature: signature,
      quote_id: quote.quoteId,
      ...getPermitPayload(permit, quote, (permit as Permit2Details).nonces ?? []),
    }),
    mode: "cors",
    cache: "no-cache",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json; charset=utf-8" },
  })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        GlobalStore.update(s => {
          s.orderError = {
            error: {
              errorCode: response.status,
              message: "HTTP Status Code",
            },
          };
          s.modalSelector = undefined;
          s.orderStatus = OrderStatus.ERROR;
        });
        Sentry.captureException(`Post Order returned a bad response`, {
          extra: { error: JSON.stringify(response) },
        });
      }
      return Promise.resolve(undefined);
    })
    .then((json: Order | OrderError) => {
      if (isOrderError(json)) {
        console.log(getErrorMessageFromOrderError(json));
        Sentry.captureException(`THERE WAS AN ORDER ERROR`, {
          extra: { error: JSON.stringify(json) },
        });
        GlobalStore.update(s => {
          s.orderError = json;
          s.orderStatus = OrderStatus.ERROR;
          s.modalSelector = undefined;
        });
        return undefined;
      }
      console.log("Successful Order", JSON.stringify(json));
      return json;
    })
    .catch((error: Error) => {
      Sentry.captureException(`Error with sendOrder`, { extra: { error: error } });
      GlobalStore.update(s => {
        s.orderStatus = OrderStatus.ERROR;
        s.modalSelector = undefined;
      });
      return undefined;
    });
};

const isOrderError = (maybeOrder: Order | OrderError): maybeOrder is OrderError => {
  return "error" in maybeOrder;
};
